import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMemo } from "react";

interface PasswordStrengthIndicatorProps {
  password: string;
  show?: boolean;
}

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const requirements: PasswordRequirement[] = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "Contains uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "Contains lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "Contains number", test: (p) => /\d/.test(p) },
];

export function PasswordStrengthIndicator({ password, show = true }: PasswordStrengthIndicatorProps) {
  const { strength, metRequirements } = useMemo(() => {
    const met = requirements.filter(req => req.test(password)).length;
    const total = requirements.length;
    const strengthPercent = (met / total) * 100;
    
    return {
      strength: strengthPercent,
      metRequirements: met
    };
  }, [password]);

  const getStrengthColor = () => {
    if (strength >= 100) return "bg-success";
    if (strength >= 75) return "bg-warning";
    if (strength >= 50) return "bg-orange-500";
    return "bg-destructive";
  };

  const getStrengthLabel = () => {
    if (strength >= 100) return "Strong";
    if (strength >= 75) return "Good";
    if (strength >= 50) return "Fair";
    return "Weak";
  };

  if (!show || !password) return null;

  return (
    <div className="mt-2 space-y-4">
      {/* Strength Bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Password strength</span>
          <span className={cn(
            "font-medium",
            strength >= 100 ? "text-success" : strength >= 75 ? "text-warning" : strength >= 50 ? "text-orange-600" : "text-destructive"
          )}>
            {getStrengthLabel()}
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div 
            className={cn("h-full transition-all duration-300", getStrengthColor())}
            style={{ width: `${strength}%` }}
          />
        </div>
      </div>

      {/* Requirements Checklist */}
      <div className="space-y-1.5">
        {requirements.map((req, index) => {
          const isMet = req.test(password);
          return (
            <div key={index} className="flex items-center gap-2 text-xs">
              {isMet ? (
                <Check className="size-3.5 shrink-0 text-success" />
              ) : (
                <X className="size-3.5 shrink-0 text-muted-foreground/40" />
              )}
              <span className={cn(
                isMet ? "text-success font-medium" : "text-muted-foreground"
              )}>
                {req.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
